"use client";

import React, { useState, useEffect, useCallback, startTransition } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { ArrowLeft, CheckCircle, AlertCircle, MessageSquare } from "lucide-react";
import { DocumentManager } from "../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { ConfirmationDialog } from "../ui/confirmation-dialog";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { ReviewComment } from "../shared/ReviewComment";

const AREA_NAME = "Employee Benefits Expense";

interface TailoringQuestion {
  id: string;
  question: string;
  type: string;
  options?: string[];
  follow_up?: {
    condition: string;
    id: string;
    question: string;
    type: string;
    options?: string[];
  };
}

interface TailoringQuestionsProps {
  onBack?: () => void;
  onComplete?: (answers: Record<string, string>) => void;
}

interface QuestionResponse {
  selectedOption?: string;
  textResponse?: {
    html: string;
    plainText: string;
  } | null;
  documents?: string[];
  table?: any[];
}

export default function TailoringQuestions({
  onBack,
  onComplete,
}: TailoringQuestionsProps) {
  const { isArchivalMode } = useArchivalMode();
  const [questions, setQuestions] = useState<TailoringQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>("");
  const [questionResponses, setQuestionResponses] = useState<Record<string, QuestionResponse>>({});
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  
  // Prevent edit mode in archival mode
  useEffect(() => {
    if (isArchivalMode && isEditMode) {
      setIsEditMode(false);
    }
  }, [isArchivalMode, isEditMode]);
  const [sharePointSaveStatus, setSharePointSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [sharePointSaveError, setSharePointSaveError] = useState<string | null>(null);
  const [showStopEditingDialog, setShowStopEditingDialog] = useState<boolean>(false);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("");
      }
    };
    loadCompanyName();
  }, []);

  // Load tailoring questions and responses from SharePoint
  useEffect(() => {
    const loadData = async () => {
      if (!companyName) {
        console.log('[TailoringQuestions] Waiting for company name...');
        return;
      }
      
      console.log('[TailoringQuestions] Loading data for company:', companyName);
      setLoading(true);
      setError(null);
      
      try {
        // Hardcoded tailoring questions for Employee Benefit Expense
        const tailoringQuestions: TailoringQuestion[] = [
          {
            id: "Payroll-1",
            question: "Does the entity have any accounting estimates relating to payroll? (for eg. gratuity, other employee benefits, etc.)",
            type: "radio",
            options: ["Yes", "No"],
            follow_up: {
              condition: "Yes",
              id: "Payroll-2",
              question: "Does the management use an expert for these estimates?",
              type: "radio",
              options: ["Yes", "No"]
            }
          },
          {
            id: "Payroll-3",
            question: "Has the entity capitalized any portion of its salary for ongoing/completed projects?",
            type: "radio",
            options: ["Yes", "No"]
          }
        ];
        
        setQuestions(tailoringQuestions);
        
        // Load responses directly from SharePoint file: Execution_TailoringQuestions_Responses.json
        // This works in both normal and archival mode (read operation)
        console.log('[TailoringQuestions] Attempting to load responses from SharePoint file: Execution_TailoringQuestions_Responses.json...');
        if (window.sharePointAPI?.downloadClientJson) {
          const result = await window.sharePointAPI.downloadClientJson('Execution_TailoringQuestions_Responses.json');

          console.log('[TailoringQuestions] Load result:', {
            success: result?.success,
            hasData: !!result?.data,
            dataKeys: result?.data ? Object.keys(result.data) : [],
            areaName: AREA_NAME
          });

          if (result.success && result.data) {
            // Extract data for the Employee Benefits Expense area
            const areaData = result.data[AREA_NAME];
            
            if (areaData) {
              const loadedResponses: Record<string, QuestionResponse> = {};
              const loadedAnswers: Record<string, string> = {};
              
              for (const question of tailoringQuestions) {
                const questionData = areaData[question.id];
                console.log(`[TailoringQuestions] Processing question ${question.id}, data:`, questionData);
                
                if (questionData) {
                  loadedResponses[question.id] = {
                    selectedOption: questionData.selectedOption || "",
                    textResponse: questionData.textResponse || null,
                    documents: Array.isArray(questionData.documents) ? questionData.documents : (questionData.documents ? [questionData.documents] : []),
                    table: Array.isArray(questionData.table) ? questionData.table : (questionData.table ? [questionData.table] : [])
                  };
              
                  // Load selectedOption to answers state for display in RadioGroup/Textarea
                  if (questionData.selectedOption) {
                    loadedAnswers[question.id] = String(questionData.selectedOption);
                    console.log(`[TailoringQuestions]  Loaded answer for ${question.id}: "${questionData.selectedOption}"`);
                  } else if (question.type === "text" && questionData.textResponse?.plainText) {
                    // For text questions, use plainText if available
                    loadedAnswers[question.id] = questionData.textResponse.plainText;
                    console.log(`[TailoringQuestions]  Loaded text answer for ${question.id}`);
                  }
                }
                
                // Also check follow-up questions
                if (question.follow_up) {
                  const followUpData = areaData[question.follow_up.id];
                  if (followUpData) {
                    loadedResponses[question.follow_up.id] = {
                      selectedOption: followUpData.selectedOption || "",
                      textResponse: followUpData.textResponse || null,
                      documents: Array.isArray(followUpData.documents) ? followUpData.documents : (followUpData.documents ? [followUpData.documents] : []),
                      table: Array.isArray(followUpData.table) ? followUpData.table : (followUpData.table ? [followUpData.table] : [])
                    };
                    // Load selectedOption to answers state for display in RadioGroup
                    if (followUpData.selectedOption) {
                      loadedAnswers[question.follow_up.id] = String(followUpData.selectedOption);
                      console.log(`[TailoringQuestions]  Loaded answer for follow-up ${question.follow_up.id}: "${followUpData.selectedOption}"`);
                    }
                  }
                }
              }
              
              console.log('[TailoringQuestions]  Final loadedAnswers object:', loadedAnswers);
              console.log('[TailoringQuestions]  Final loadedResponses keys:', Object.keys(loadedResponses));
              console.log('[TailoringQuestions]  About to call setAnswers with:', JSON.stringify(loadedAnswers, null, 2));
              
              // Set both states
              setQuestionResponses(loadedResponses);
              setAnswers(loadedAnswers);
              
              console.log('[TailoringQuestions]  Successfully loaded and set responses for ' + Object.keys(loadedAnswers).length + ' questions');
              console.log('[TailoringQuestions]  States have been set. Answers should now display in UI.');
            } else {
              console.log(`[TailoringQuestions]  No data found for area "${AREA_NAME}" in Execution_TailoringQuestions_Responses.json`);
              setQuestionResponses({});
              setAnswers({});
            }
          } else {
            console.log('[TailoringQuestions]  No responses found in SharePoint file Execution_TailoringQuestions_Responses.json');
            setQuestionResponses({});
            setAnswers({});
          }
        } else {
          console.warn('[TailoringQuestions] window.sharePointAPI.downloadClientJson is not available');
          setQuestionResponses({});
          setAnswers({});
        }
      } catch (error) {
        console.error('[TailoringQuestions] Failed to load responses from SharePoint:', error);
        setError(error instanceof Error ? error.message : 'Failed to load responses from SharePoint');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [companyName]);

  // Debug: Log when answers change to verify state updates
  useEffect(() => {
    console.log('[TailoringQuestions] Answers state updated:', answers);
    console.log('[TailoringQuestions] Current answers values:', JSON.stringify(answers, null, 2));
  }, [answers]);

  // Save responses to SharePoint using tailoring questions API
  const saveResponses = useCallback(async () => {
    if (!companyName) return;
    
    try {
      // Build responses object in the format expected by the API
      const responsesToSave: Record<string, any> = {};
      
      // Load existing responses first to preserve data for questions we're not updating
      let existingResponses: Record<string, any> = {};
      if (window.tailoringQuestionsAPI?.loadResponses) {
                try {
          const loadResult = await window.tailoringQuestionsAPI.loadResponses({
            companyName,
            areaName: AREA_NAME
          });
          
          if (loadResult.success && loadResult.data) {
            existingResponses = loadResult.data;
            console.log('[TailoringQuestions] Loaded existing responses for merge:', Object.keys(existingResponses));
          }
        } catch (loadError) {
          console.warn('[TailoringQuestions] Could not load existing responses, will create new:', loadError);
        }
      }
      
      // Build responses for all questions we have data for
      for (const question of questions) {
        const questionId = question.id;
        const currentResponse = questionResponses[questionId] || {};
        const answer = answers[questionId] || "";
        
        // Only include if we have data or if it exists in existing responses
        const hasNewData = answer || currentResponse.selectedOption || currentResponse.textResponse || currentResponse.documents || currentResponse.table;
        const hasExistingData = existingResponses[questionId] !== undefined;
        
        if (hasNewData || hasExistingData) {
          // Non-destructive merge: preserve existing fields, update with new data
          responsesToSave[questionId] = {
            ...(existingResponses[questionId] || {}), // Preserve existing fields
            selectedOption: answer || currentResponse.selectedOption || existingResponses[questionId]?.selectedOption || "",
            textResponse: currentResponse.textResponse !== undefined ? currentResponse.textResponse : (existingResponses[questionId]?.textResponse || null),
            documents: currentResponse.documents !== undefined ? currentResponse.documents : (existingResponses[questionId]?.documents || []),
            table: currentResponse.table !== undefined ? currentResponse.table : (existingResponses[questionId]?.table || [])
          };
        }
        
        // Also handle follow-up questions
        if (question.follow_up) {
          const followUpId = question.follow_up.id;
          const followUpResponse = questionResponses[followUpId] || {};
          const followUpAnswer = answers[followUpId] || "";
          
          const hasNewFollowUpData = followUpAnswer || followUpResponse.selectedOption || followUpResponse.textResponse || followUpResponse.documents || followUpResponse.table;
          const hasExistingFollowUpData = existingResponses[followUpId] !== undefined;
          
          if (hasNewFollowUpData || hasExistingFollowUpData) {
            responsesToSave[followUpId] = {
              ...(existingResponses[followUpId] || {}), // Preserve existing fields
              selectedOption: followUpAnswer || followUpResponse.selectedOption || existingResponses[followUpId]?.selectedOption || "",
              textResponse: followUpResponse.textResponse !== undefined ? followUpResponse.textResponse : (existingResponses[followUpId]?.textResponse || null),
              documents: followUpResponse.documents !== undefined ? followUpResponse.documents : (existingResponses[followUpId]?.documents || []),
              table: followUpResponse.table !== undefined ? followUpResponse.table : (existingResponses[followUpId]?.table || [])
            };
          }
        }
      }
      
      // Log changes before saving
      const changedQuestions = Object.keys(responsesToSave).filter(qId => {
        const existing = existingResponses[qId];
        const newData = responsesToSave[qId];
        return !existing || JSON.stringify(existing) !== JSON.stringify(newData);
      });
      
      if (changedQuestions.length > 0) {
        console.log(`[TailoringQuestions] Changes detected for questions:`, changedQuestions);
        console.log(`[TailoringQuestions] Saving ${Object.keys(responsesToSave).length} question responses`);
      } else {
        console.log(`[TailoringQuestions] No changes detected - responses are up to date`);
      }
      
      // Save using tailoring questions API
      if (window.tailoringQuestionsAPI?.saveResponses) {
        const result = await window.tailoringQuestionsAPI.saveResponses({
          companyName,
          areaName: AREA_NAME,
          responses: responsesToSave
        });
        
        if (!result.success) {
          throw new Error(result.error || "Failed to save");
        }
        
        console.log(`[TailoringQuestions] Successfully saved responses to SharePoint`);
      } else {
        throw new Error("Tailoring Questions API not available");
      }
    } catch (error) {
      console.error('[TailoringQuestions] Failed to save responses to SharePoint:', error);
      throw error;
    }
  }, [companyName, questions, answers, questionResponses]);

  const handleAnswerChange = (questionId: string, answer: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
    
    // Update question response
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        selectedOption: answer
      }
    }));
  };
  
  const handleDocumentsChange = (questionId: string, documents: any[]) => {
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        documents: documents.map(doc => doc.name || doc)
      }
    }));
  };
  
  const handleTextResponseChange = (questionId: string, textResponse: any) => {
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        textResponse
      }
    }));
  };

  const handleComplete = async () => {
    // Save before completing if in edit mode
    if (isEditMode) {
      setSharePointSaveStatus('saving');
      try {
        await saveResponses();
        setSharePointSaveStatus('saved');
        setIsEditMode(false);
      } catch (error) {
        setSharePointSaveStatus('error');
        setSharePointSaveError(error instanceof Error ? error.message : 'Save failed');
        return;
      }
    }
    
    if (onComplete) {
      onComplete(answers);
    }
  };

  const isQuestionAnswered = (questionId: string) => {
    const hasOptionAnswer =
      answers[questionId] !== undefined && answers[questionId] !== "";

    const response = questionResponses[questionId];
    const hasTextResponse =
      !!response?.textResponse &&
      (
        (response.textResponse.html && response.textResponse.html.trim() !== "") ||
        (response.textResponse.plainText && response.textResponse.plainText.trim() !== "")
      );
    const hasDocuments =
      !!response?.documents && Array.isArray(response.documents) && response.documents.length > 0;
    const hasTable =
      !!response?.table && Array.isArray(response.table) && response.table.length > 0;

    return hasOptionAnswer || hasTextResponse || hasDocuments || hasTable;
  };

  const shouldShowFollowUp = (question: TailoringQuestion) => {
    if (!question.follow_up) return false;
    return answers[question.id] === question.follow_up.condition;
  };

  const allQuestionsAnswered = () => {
    return questions.every(q => {
      const mainAnswered = isQuestionAnswered(q.id);
      const followUpAnswered = !shouldShowFollowUp(q) || isQuestionAnswered(q.follow_up!.id);
      return mainAnswered && followUpAnswered;
    });
  };

  const renderQuestion = (question: TailoringQuestion, isFollowUp = false) => {
    const isAnswered = isQuestionAnswered(question.id);
    
    return (
      <Card key={question.id} className={`border-white/10 bg-white/5 text-white ${isFollowUp ? 'ml-6 border-l-2 border-l-[#4da3ff]/30' : ''}`}>
        <CardContent className="p-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                {isAnswered ? (
                  <CheckCircle className="h-5 w-5 text-green-400" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-white/40" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-start gap-2">
                  <Label className="text-sm font-medium text-white flex-1">
                    {question.question}
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setReviewCommentQuestionId(question.id)}
                    className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                    title="Review Comment"
                  >
                    <MessageSquare className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {question.type === "radio" && question.options && (
              <RadioGroup
                value={answers[question.id] || ""}
                onValueChange={(value) => handleAnswerChange(question.id, value)}
                disabled={!isEditMode || isArchivalMode}
                className="ml-8"
              >
                {question.options.map((option) => (
                  <div key={option} className="flex items-center space-x-2">
                    <RadioGroupItem 
                      value={option} 
                      id={`${question.id}-${option}`}
                      className="border-white/20 text-[#4da3ff] focus:ring-[#4da3ff]/20"
                    />
                    <Label 
                      htmlFor={`${question.id}-${option}`}
                      className="text-sm text-white/80 cursor-pointer"
                    >
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}

            {question.type === "text" && (
              <div className="ml-8">
                <Textarea
                  placeholder="Enter your answer here..."
                  value={answers[question.id] || ""}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  disabled={!isEditMode || isArchivalMode}
                  className="min-h-[80px] bg-white/5 border-white/20 text-white placeholder:text-white/50 focus:border-[#4da3ff]/50 focus:ring-[#4da3ff]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            )}
            
            {(question.type === "document" || question.type === "text") && (
              <div className="ml-8 mt-3">
                <DocumentManager
                  sectionKey={`execution-tailoring:${AREA_NAME}`}
                  itemId={question.id}
                  itemQuestion={question.question}
                  itemTextResponse={questionResponses[question.id]?.textResponse}
                  itemDocuments={questionResponses[question.id]?.documents?.map((doc: string) => ({
                    id: `doc_${doc}`,
                    type: 'linked' as const,
                    name: doc
                  })) || []}
                  onDocumentsChange={(docs) => handleDocumentsChange(question.id, docs)}
                  onTextResponseChange={(textResp) => handleTextResponseChange(question.id, textResp)}
                  disabled={!isEditMode || isArchivalMode}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          )}
          <div>
            <h2 className="text-xl font-semibold text-white">Tailoring Questions</h2>
            <p className="text-sm text-white/60">Loading questions...</p>
          </div>
        </div>
        
        <Card className="border-white/10 bg-white/5 text-white">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4da3ff] mx-auto mb-4"></div>
              <p>Loading tailoring questions...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          )}
          <div>
            <h2 className="text-xl font-semibold text-white">Tailoring Questions</h2>
            <p className="text-sm text-white/60">Error loading questions</p>
          </div>
        </div>
        
        <Card className="border-red-500/20 bg-red-500/5 text-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div>
                <h3 className="font-medium text-red-400">Error</h3>
                <p className="text-sm text-red-300">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          )}
          <div>
            <h2 className="text-xl font-semibold text-white">Tailoring Questions</h2>
            <p className="text-sm text-white/60">
              Answer these questions to tailor the audit approach for payroll
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isEditMode ? (
            <Button
              onClick={() => {
                startTransition(() => {
                  setIsEditMode(true);
                });
              }}
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/10"
              disabled={isArchivalMode}
            >
              Edit
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                onClick={async () => {
                  setSharePointSaveStatus('saving');
                  try {
                    await saveResponses();
                    setSharePointSaveStatus('saved');
                    setIsEditMode(false);
                    setTimeout(() => setSharePointSaveStatus('idle'), 3000);
                  } catch (error) {
                    setSharePointSaveStatus('error');
                    setSharePointSaveError(error instanceof Error ? error.message : 'Save failed');
                  }
                }}
                disabled={sharePointSaveStatus === 'saving' || isArchivalMode}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {sharePointSaveStatus === 'saving' ? 'Saving...' : 'Save'}
              </Button>
              <Button
                onClick={() => setShowStopEditingDialog(true)}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/10"
                disabled={isArchivalMode}
              >
                Stop Editing
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Save Status Messages */}
      {sharePointSaveStatus === 'saved' && (
        <div className="rounded-md bg-green-500/10 border border-green-500/20 p-3 text-sm text-green-300">
          Changes saved successfully
        </div>
      )}
      {sharePointSaveStatus === 'error' && sharePointSaveError && (
        <div className="rounded-md bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-300">
          Error saving changes: {sharePointSaveError}
        </div>
      )}

      {/* Progress Indicator */}
      <Card className="border-white/10 bg-white/5 text-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-white">Progress</span>
              <Badge variant="outline" className="text-xs">
                {questions.filter(q => isQuestionAnswered(q.id)).length} / {questions.length} answered
              </Badge>
            </div>
            <div className="text-xs text-white/60">
              {Math.round((questions.filter(q => isQuestionAnswered(q.id)).length / questions.length) * 100)}% complete
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Questions */}
      <div className="space-y-4">
        {questions.map((question) => (
          <div key={question.id} className="space-y-3">
            {renderQuestion(question)}
            {shouldShowFollowUp(question) && question.follow_up && (
              <div className="transition-all duration-300">
                {renderQuestion(question.follow_up, true)}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer Actions */}
      <Card className="border-white/10 bg-white/5 text-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-white/60">
              {allQuestionsAnswered() 
                ? "All questions completed. You can proceed to the next step."
                : "Please answer all questions to proceed."
              }
            </div>
            <Button
              onClick={handleComplete}
              disabled={!allQuestionsAnswered() || isArchivalMode}
              className="bg-[#4da3ff] text-white hover:bg-[#4da3ff]/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Complete Assessment
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Stop Editing Confirmation Dialog */}
      <ConfirmationDialog
        open={showStopEditingDialog}
        onOpenChange={setShowStopEditingDialog}
        title="Stop Editing"
        description="Do you want to save your changes before stopping editing?"
        onYes={async () => {
          setShowStopEditingDialog(false);
          setSharePointSaveStatus('saving');
          try {
            await saveResponses();
            setSharePointSaveStatus('saved');
            setIsEditMode(false);
            setTimeout(() => setSharePointSaveStatus('idle'), 3000);
          } catch (error) {
            setSharePointSaveStatus('error');
            setSharePointSaveError(error instanceof Error ? error.message : 'Save failed');
            alert("Failed to save changes. Please try again.");
          }
        }}
        onNo={() => {
          setShowStopEditingDialog(false);
          setIsEditMode(false);
        }}
        onCancel={() => {
          setShowStopEditingDialog(false);
        }}
        yesText="Yes"
        noText="No"
        cancelText="Cancel"
      />
      
      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={questions.find(q => q.id === reviewCommentQuestionId)?.question}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}
